/**
 * Command registry.
 *
 * Backs the command palette. Descriptors (serialisable — label/group/keywords)
 * live in a Valtio proxy so the palette can render + filter them reactively;
 * their handlers live in a plain, non-proxied Map so Valtio never deep-proxies
 * functions. Producers register on mount and unregister on unmount via
 * `useRegisterCommands`. Registering an id that already exists replaces it, so a
 * producer can re-register with fresh closures when its inputs change.
 */

import { proxy } from 'valtio';
import type { CommandDescriptor, CommandId } from './keybinding.types';

export type CommandRegistryState = { commands: CommandDescriptor[] };

export const commandRegistryState = proxy<CommandRegistryState>({ commands: [] });

const handlers = new Map<CommandId, () => void>();

const register = (entries: { descriptor: CommandDescriptor; run: () => void }[]): (() => void) => {
  const ids = new Set(entries.map((e) => e.descriptor.id));
  for (const { descriptor, run } of entries) {
    handlers.set(descriptor.id, run);
  }
  commandRegistryState.commands = [
    ...commandRegistryState.commands.filter((c) => !ids.has(c.id)),
    ...entries.map((e) => e.descriptor),
  ];
  return () => {
    for (const id of ids) {
      handlers.delete(id);
    }
    commandRegistryState.commands = commandRegistryState.commands.filter((c) => !ids.has(c.id));
  };
};

const run = (id: CommandId): void => {
  handlers.get(id)?.();
};

export const commandRegistryActions = Object.freeze({ register, run });

export type CommandRegistryActions = typeof commandRegistryActions;
